import { getDownloadAccess } from '../utils/downloadAccess';
import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../supabase';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Fuse from 'fuse.js';
import AdBanner from '../components/AdBanner';
import DownloadPromptModal from '../components/DownloadPromptModal';
import { handleSongDownload } from '../utils/downloadHelper';
import AudioDownloadModal from '../components/AudioDownloadModal';
import '../styles.css';

// Simple UUID generator
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

// Counter animation hook
const useCounter = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const increment = end / (duration / 16); // 60 FPS
    const step = () => {
      start += increment;
      if (start >= end) {
        setCount(end);
      } else {
        setCount(Math.floor(start));
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [end, duration]);
  return count;
};

function Home() {
  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [downloadPrompt, setDownloadPrompt] = useState(null);
  const [songOfTheWeek, setSongOfTheWeek] = useState(null);
  const [songTitle, setSongTitle] = useState('');
  const [songComposer, setSongComposer] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [recentDownloads, setRecentDownloads] = useState([]);
  const [siteStats, setSiteStats] = useState({
    totalSongs: 0,
    totalDownloads: 0,
  });
  const audioRef = useRef(null);
  const searchInputRef = useRef(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptType, setPromptType] = useState('register');
  const [selectedSong, setSelectedSong] = useState(null);
  const [user, setUser] = useState(null);
  const [downloadCountThisMonth, setDownloadCountThisMonth] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [audioModalOpen, setAudioModalOpen] = useState(false);
  const [selectedAudioSong, setSelectedAudioSong] = useState(null);
  // Fuzzy search indexes (Fuse.js)
  const [songFuse, setSongFuse] = useState(null);
  const [postFuse, setPostFuse] = useState(null);
  const navigate = useNavigate();
  
  const handleRegister = () => {
  setShowPrompt(false);
  navigate('/signup');
};

  const handleSubscribe = () => {
  setShowPrompt(false);
  navigate('/subscribe');
};

  const [topSongs, setTopSongs] = useState([]);

// Fetch top downloaded songs
useEffect(() => {
  const fetchTopSongs = async () => {
    const { data, error } = await supabase
      .from('songs')
      .select('id, title, composer, downloads, permalink')
      .order('downloads', { ascending: false })
      .limit(5);
    if (!error && data) setTopSongs(data);
  };
  fetchTopSongs();
}, []);

  // For referencing multiple audio elements
const audioRefs = useRef([]);

// Handle play for one of the 'other songs'
const handlePlayAudio = (idx) => {
  setPlayingIndex(idx); // Update the current playing index for UI highlight if needed
  // Pause all others
  audioRefs.current.forEach((audio, i) => {
    if (audio && i !== idx) {
      audio.pause();
      audio.currentTime = 0;
    }
  });
  // Play the selected
  const selected = audioRefs.current[idx];
  if (selected) selected.play();
};

// Handle download attempt for other audio songs
    const handleAudioDownload = (song) => {
      // Check if user is subscribed (supporter or expert, not expired)
      const isSubscribed =
        (user?.subscription_tier === 'choir_supporter' || user?.subscription_tier === 'choir_expert') &&
        user?.subscription_expires_at &&
        new Date(user.subscription_expires_at) > new Date();
    
      if (!isSubscribed) {
        setSelectedAudioSong(song);
        setAudioModalOpen(true);
        return;
      }
      // If allowed, trigger actual download (direct audio link or however you serve)
      window.open(song.audio_url, '_blank');
    };

// State for shuffled songs with audio and which is playing
const [audioSongs, setAudioSongs] = useState([]);
const [playingIndex, setPlayingIndex] = useState(-1);

// Fetch and shuffle 5 random songs with audio each reload
useEffect(() => {
  if (songs.length > 0) {
    const songsWithAudio = songs.filter(s => s.audio_url);
    // Shuffle array
    for (let i = songsWithAudio.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [songsWithAudio[i], songsWithAudio[j]] = [songsWithAudio[j], songsWithAudio[i]];
    }
    setAudioSongs(songsWithAudio.slice(0, 5));
  }
}, [songs]);

  const handleDownload = async (song) => {
  setSelectedSong(song);

  // Use the helper function!
  const result = await handleSongDownload(song, user, setPromptType, setShowPrompt);

  if (!result.allowed) return; // If not allowed, prompt will be shown

  // Proceed with download
  window.open(`https://drive.google.com/uc?export=download&id=${song.google_drive_file_id}`, '_blank');

  // Instantly update the download count in the UI
  setSongs(prev => prev.map(s => s.id === song.id ? { ...s, downloads: (s.downloads || 0) + 1 } : s));
  setFilteredSongs(prev => prev.map(s => s.id === song.id ? { ...s, downloads: (s.downloads || 0) + 1 } : s));
};

  const formatTime = (secs) => {
  if (isNaN(secs)) return "0:00";
  const minutes = Math.floor(secs / 60);
  const seconds = Math.floor(secs % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
};

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;
      const setAudioData = () => setDuration(audio.duration || 0);
      const setAudioTime = () => setCurrentTime(audio.currentTime || 0);
      audio.addEventListener("loadedmetadata", setAudioData);
      audio.addEventListener("timeupdate", setAudioTime);
      return () => {
        audio.removeEventListener("loadedmetadata", setAudioData);
        audio.removeEventListener("timeupdate", setAudioTime);
      };
    }, [songOfTheWeek]);
  
  useEffect(() => {
      const getUser = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setUser(null);
          return;
        }
        // Fetch full user profile from Supabase
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        if (error || !profile) {
          setUser({ ...session.user }); // fallback to basic
        } else {
          setUser({ ...session.user, ...profile }); // merge both!
        }
      };
      getUser();
    }, []);

  useEffect(() => {
    const fetchData = async () => {
      console.log('Fetching all data');

      const cleanAudioUrl = (url) => {
        if (!url) return null;
        const cleaned = url
          .replace(/<[^>]+>/g, '')
          .replace(/%3C[^%]+%3E/g, '');
        return cleaned.match(/\.(mp3|wav|ogg)$/) ? cleaned : null;
      };

      try {
        // Fetch songs
        const { data: songData, error: songError } = await supabase
          .from('songs')
          .select('id, title, composer, lyrics, google_drive_file_id, permalink, is_public, downloads, created_at, updated_at, audio_url')
          .eq('is_public', true)
          .order('created_at', { ascending: false });

          if (songError) {
            setError('Failed to load songs. Please try again later.');
            console.error('Song fetch error:', songError);
            return;
          }
        setSongs(songData || []);
        setFilteredSongs(songData.slice(0, 4) || []);
        setLoading(false);

        // Setup Fuse for songs (title, composer, lyrics)
        setSongFuse(
          new Fuse(songData || [], {
            keys: [
              { name: 'title', weight: 0.45 },
              { name: 'composer', weight: 0.25 },
              { name: 'lyrics', weight: 0.3 }
            ],
            threshold: 0.5,
            includeMatches: true,
            ignoreLocation: true,
            minMatchCharLength: 2,
            useExtendedSearch: true
          })
        );

        // Fetch total downloads using RPC
        const { data: totalDownloadsData, error: totalDownloadsError } = await supabase
          .rpc('get_total_downloads');
          if (totalDownloadsError) {
            setError('Failed to load download stats. Please try again later.');
            console.error('Total downloads error:', totalDownloadsError);
            return;
          }
        const totalDownloads = parseInt(totalDownloadsData || 0, 10);

        // Fetch posts
        const { data: postData, error: postError } = await supabase
          .from('blog_posts')
          .select('id, title, permalink')
          .order('created_at', { ascending: false });
          if (postError) {
            setError('Failed to load blog posts. Please try again later.');
            console.error('Blog post fetch error:', postError);
            return;
          }
        setPosts(postData || []);
        setFilteredPosts(postData.slice(0, 4) || []);

        // Setup Fuse for posts (title only)
        setPostFuse(
          new Fuse(postData || [], {
            keys: ['title'],
            threshold: 0.5,
            includeMatches: true,
            ignoreLocation: true,
            minMatchCharLength: 2,
            useExtendedSearch: true
          })
        );

        // Fetch Song of the Week
        const { data: songOfTheWeekData, error: sotwError } = await supabase
          .from('song_of_the_week')
          .select('audio_url, title, composer')
          .limit(1);
          if (sotwError) {
            setError('Failed to load Song of the Week. Please try again later.');
            console.error('Song of the Week error:', sotwError);
            return;
          }
        const audioUrl = songOfTheWeekData?.[0] ? cleanAudioUrl(songOfTheWeekData[0].audio_url) : null;
        const title = songOfTheWeekData?.[0]?.title || 'Unknown Title';
        const composer = songOfTheWeekData?.[0]?.composer || 'Unknown Composer';
        setSongOfTheWeek(audioUrl);
        setSongTitle(title);
        setSongComposer(composer);

        // Check authentication status
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) console.error('Session error:', sessionError.message);
        setIsAuthenticated(!!sessionData?.session);

        // Fetch recent downloads
        const { data: downloadData, error: downloadError } = await supabase
          .from('songs')
          .select('id, title, composer, downloads, created_at, updated_at, permalink')
          .eq('is_public', true)
          .order('updated_at', { ascending: false })
          .limit(5);
          if (downloadError) {
            setError('Failed to load recent downloads. Please try again later.');
            console.error('Recent downloads error:', downloadError);
            return;
          }
        setRecentDownloads(downloadData || []);

        // Fetch site statistics
        const { count: totalSongs, error: songCountError } = await supabase
          .from('songs')
          .select('id', { count: 'exact', head: true });
        if (songCountError) {
            setError('Failed to count songs. Please try again later.');
            console.error('Song count error:', songCountError);
            return;
          }

        setSiteStats({
          totalSongs: totalSongs || 0,
          totalDownloads,
        });
      } catch (err) {
        console.error('Fetch error:', err.message);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();

    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 5);
    }, 5000);
    return () => clearInterval(slideInterval);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const updateProgress = () => {
        if (audio.duration) {
          setProgress((audio.currentTime / audio.duration) * 100);
        }
      };
      audio.addEventListener('timeupdate', updateProgress);
      return () => audio.removeEventListener('timeupdate', updateProgress);
    }
  }, [songOfTheWeek]);

  useEffect(() => {
    filterContent(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch((err) => {
          setError(`Failed to play audio: ${err.message}`);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const rewind = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => {
        setError(`Failed to play audio: ${err.message}`);
        });
      setIsPlaying(true);
    }
  };

  const normalize = str => (str || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')   // Remove accents/diacritics
  .replace(/[\p{P}$+<=>^`|~]/gu, '') // Remove punctuation
  .trim();

const highlight = (text, matches, key) => {
  if (!matches) return text;
  const match = matches.find(m => m.key === key);
  if (!match) return text;
  let result = '';
  let lastIndex = 0;
  match.indices.forEach(([start, end], i) => {
    result += text.slice(lastIndex, start);
    result += `<mark style="background:rgba(163,230,53,0.18); color:inherit; border-radius:4px; padding:0 2px;">${text.slice(start, end + 1)}</mark>`;
    lastIndex = end + 1;
  });
  result += text.slice(lastIndex);
  return result;
};

  const filterContent = (query) => {
    const trimmedQuery = normalize(query);
    if (!trimmedQuery) {
      setFilteredSongs(songs.slice(0, 10));
      setFilteredPosts(posts.slice(0, 10));
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
      setError(null);
      return;
    }

    let songResults = [];
    let postResults = [];

    // 1. Exact substring matches (case and punctuation insensitive)
    const exactSongMatches = songs.filter(song =>
      normalize(song.title).includes(trimmedQuery) ||
      normalize(song.composer).includes(trimmedQuery) ||
      normalize(song.lyrics).includes(trimmedQuery)
    );

    const exactPostMatches = posts.filter(post =>
      normalize(post.title).includes(trimmedQuery)
    );

    // 2. Fuzzy results (with Fuse)
    let fuseSongResults = [];
    let fusePostResults = [];
    if (songFuse) {
      fuseSongResults = songFuse.search(trimmedQuery, { limit: 10 });
    }
    if (postFuse) {
      fusePostResults = postFuse.search(trimmedQuery, { limit: 10 });
    }

    // 3. Build unique suggestions: exact matches FIRST, then fuse results NOT already included
    const seenSongIds = new Set();
    const allSongSuggestions = [];

    // Add exact matches (without Fuse highlighting)
    for (const song of exactSongMatches) {
      allSongSuggestions.push({
        type: 'song',
        title: song.title,
        id: song.id,
        permalink: song.permalink,
        matches: [], // no highlight for exact
        original: song,
      });
      seenSongIds.add(song.id);
    }

    // Add fuzzy results (skip those already present)
    for (const r of fuseSongResults) {
      if (!seenSongIds.has(r.item.id)) {
        allSongSuggestions.push({
          type: 'song',
          title: r.item.title,
          id: r.item.id,
          permalink: r.item.permalink,
          matches: r.matches,
          original: r.item,
        });
        seenSongIds.add(r.item.id);
      }
    }

    // Same for blog posts
    const seenPostIds = new Set();
    const allPostSuggestions = [];
    for (const post of exactPostMatches) {
      allPostSuggestions.push({
        type: 'post',
        title: post.title,
        id: post.id,
        permalink: post.permalink,
        matches: [],
        original: post,
      });
      seenPostIds.add(post.id);
    }
    for (const r of fusePostResults) {
      if (!seenPostIds.has(r.item.id)) {
        allPostSuggestions.push({
          type: 'post',
          title: r.item.title,
          id: r.item.id,
          permalink: r.item.permalink,
          matches: r.matches,
          original: r.item,
        });
        seenPostIds.add(r.item.id);
      }
    }

    // Limit to 10 total suggestions (songs + posts)
    const allSuggestions = [...allSongSuggestions, ...allPostSuggestions].slice(0, 10);

    setFilteredSongs(allSongSuggestions.map(s => s.original).slice(0, 10));
    setFilteredPosts(allPostSuggestions.map(p => p.original).slice(0, 10));
    setSuggestions(allSuggestions);
    setSelectedSuggestionIndex(-1);
    setError(
      allSongSuggestions.length === 0 && allPostSuggestions.length === 0
        ? `No results found for "${query}".`
        : null
    );
  };

  const handleSearch = (e) => {
  e.preventDefault();
  const honeypot = e.target.elements.honeypot.value;
  if (honeypot) {
    setError('Spam detected');
    return;
  }
  if (!searchQuery.trim()) {
    setError('Please enter a search term');
    return;
  }

  // Fire GA4 event for search
  if (window.gtag) {
    window.gtag('event', 'site_search', {
      event_category: 'Search',
      event_label: searchQuery,
      search_term: searchQuery,
      value: 1,
    });
  }

  navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
  setSuggestions([]);
};

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Enter') {
      const honeypot = document.querySelector('input[name="honeypot"]').value;
      if (honeypot) {
        setError('Spam detected');
        return;
      }
      if (!searchQuery.trim()) {
        setError('Please enter a search term');
        return;
      }
      navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
      setSuggestions([]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > -1 ? prev - 1 : prev));
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.title);
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
    navigate(
      suggestion.type === 'song'
        ? `/song/${suggestion.permalink || suggestion.id}`
        : `/blog/${suggestion.permalink || `post-${suggestion.id}`}`
    );
    searchInputRef.current.blur();
  };

  const handleSongClick = (song) => {
    navigate(`/song/${song.permalink || song.id}`);
  };

  const handleShare = (songTitle, songId) => {
    const shareUrl = `${window.location.origin}/song/${songId}`;
    const shareText = `Check out "${songTitle}" on Choir Center!`;
    if (navigator.share) {
      navigator.share({ title: songTitle, text: shareText, url: shareUrl });
    } else {
      navigator.clipboard.writeText(shareUrl).then(() => alert('Link copied to clipboard!'));
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const calculateTimeAgo = (timestamp) => {
    const now = new Date();
    // Parse timestamp as UTC explicitly
    const updated = new Date(timestamp + (timestamp.endsWith('Z') ? '' : 'Z'));
    
    // Debug: Log raw and parsed timestamps
    console.log('Raw timestamp:', timestamp);
    console.log('Parsed updated:', updated.toISOString(), 'Now:', now.toISOString());
    
    const diffMs = now - updated;
    const minutes = Math.floor(diffMs / (1000 * 60));
    
    if (minutes < 60) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours} hour${hours === 1 ? '' : 's'} ago`;
      }
      return `${hours}hr ${remainingMinutes}min ago`;
    }
    
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  };

  const songsCount = useCounter(siteStats.totalSongs, 2000);
  const downloadsCount = useCounter(siteStats.totalDownloads, 2000);

  const galleryImages = [
    {
      src: "/gallery/choirmusicbooks.webp",
      link: "/choir-music-books",
      alt: "Choir Music Books"
    },
    {
      src: "/gallery/choirgames.webp",
      link: "/choir-games",
      alt: "Choir Games"
    },
    {
      src: "/gallery/selectionsmanager.webp",
      link: "/selections-manager",
      alt: "Selections Manager"
    },
    {
      src: "/gallery/choirevents.webp",
      link: "/choir-events",
      alt: "Choir Events"
    },
  ];

  return (
    <>
    <Helmet>
      <title>Choir Center | Free Solfa Notation Choir Songs & Blog</title>
      <meta name="description" content="Discover free choir songs in solfa notation. Download sheet music, explore chorister resources, and read expert choir blogs on Choir Center." />
      <meta name="keywords" content="solfa choir songs, free choir music, choir sheet music download, chorister blog, choir training, vocal tips" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href="https://choircenter.com" />

      <meta property="og:title" content="Choir Center - Solfa Notation Choir Songs & Tips" />
      <meta property="og:description" content="Free solfa notation songs and vocal resources for choir lovers. Start exploring now!" />
      <meta property="og:image" content="https://choircenter.com/images/choir-hero.webp" />
      <meta property="og:url" content="https://choircenter.com" />
      <meta property="og:type" content="website" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Choir Center - Solfa Notation Choir Songs" />
      <meta name="twitter:description" content="Discover choir songs in solfa notation, resources for choristers, and blogs to improve your singing." />
      <meta name="twitter:image" content="https://choircenter.com/images/choir-hero.webp" />

      <link rel="preload" href="/assets/index-mRxUMGiO.css" as="style" />
      <link rel="stylesheet" href="/assets/index-mRxUMGiO.css" media="print" onload="this.media='all';this.onload=null" />
      
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap"
        media="print"
        onload="this.media='all';this.onload=null"
      />
      
      <link rel="icon" href="/favicon.ico" />

      <link rel="preconnect" href={import.meta.env.VITE_SUPABASE_URL} />
      <link rel="icon" href="/favicon.ico" />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MusicGroup",
          "name": "Choir Center",
          "url": "https://choircenter.com",
          "description": "Free choir music, solfa notation songs, and blog resources for choristers worldwide.",
          "image": "https://choircenter.com/images/logo.png"
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is Choir Center and who is it for?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Choir Center is a free online platform for choir singers, directors, and music lovers. We offer downloadable solfa notation choir songs, rehearsal tips, and music learning resources for both beginners and experienced choristers."
              }
            },
            {
              "@type": "Question",
              "name": "Are the choir songs really free to download?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes! Every solfa notation choir song on our site is free to download for practice, worship, and performance. You can access and print sheet music anytime. Terms and conditions apply."
              }
            },
            {
              "@type": "Question",
              "name": "How do I download a choir song?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Simply browse the choir library, click on any song, and use the Download button. Some downloads may require free registration for security and to help us keep offering great music."
              }
            },
            {
              "@type": "Question",
              "name": "What is solfa notation?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Solfa notation (do, re, mi, fa, sol, la, ti) is a simple way to read and sing music. If you're new, start with our beginner guide, and enjoy easy-to-follow choir sheet music!"
              }
            },
            {
              "@type": "Question",
              "name": "Can I request or submit a choir song?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes! Use our contact form or email us to request new songs or submit your own compositions. We love featuring community submissions from all traditions."
              }
            },
            {
              "@type": "Question",
              "name": "Do you offer choir rehearsal or vocal training tips?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutely. Check our blog for expert tips on sight-singing, conducting, voice training, and music theory, all written for choirs and vocal groups."
              }
            },
            {
              "@type": "Question",
              "name": "Is Choir Center mobile-friendly?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes! Our website works great on phones, tablets, and computers. Download choir songs, follow lessons, and practice anywhere, anytime."
              }
            }
          ]
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "url": "https://choircenter.com",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://choircenter.com/search?query={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}
      </script>            
    </Helmet>

    <section
      className="relative left-1/2 right-1/2 -mx-[50vw] w-screen min-h-[460px] md:min-h-[520px] xl:min-h-[620px] flex items-center justify-center overflow-visible"
      style={{
        fontFamily: `'Sora', 'Inter', 'Helvetica Neue', 'Arial', sans-serif'`,
        left: '50%',
        right: '50%',
      }}
    >
      {/* Slideshow background */}
      <div className="absolute inset-0 w-full h-full z-0">
        {[1, 2, 3, 4, 5].map((num) => (
          <img
            key={num}
            src={`/images/choir${num}.webp`}
            alt={`Choir singing at Choir Center ${num}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              currentSlide === num - 1 ? 'opacity-100' : 'opacity-0'
            }`}
            loading={num === 1 ? "eager" : "lazy"} // Only the first image loads immediately
            fetchpriority={num === 1 ? "high" : "low"}
            draggable={false}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-green-900/60 to-green-900/85"></div>
      </div>
    
      {/* Hero Content */}
      <div
        className="
          relative z-10 flex flex-col items-center w-full 
          px-3 sm:px-6 md:px-8 text-center 
          max-w-[98vw] sm:max-w-[680px] lg:max-w-[900px] xl:max-w-[1100px] 
          mx-auto
        "
      >
        <h1
          className="
            font-black text-white drop-shadow-xl 
            text-[2.1rem] sm:text-3xl md:text-5xl xl:text-6xl 
            leading-[1.08] tracking-tighter mb-4 animate-text w-full
          "
          style={{ fontFamily: `'Sora', 'Inter', sans-serif'` }}
        >
          Free Solfa Notation <span className="text-green-300 bg-black/20 rounded px-2">Choir Songs</span>
          <br />
          and Vocal Resources
        </h1>
        <p className="text-green-100 text-base sm:text-lg font-medium drop-shadow mt-1 mb-5 animate-text max-w-xl mx-auto">
          Access free choir songs in solfa notation, download sheet music, and improve your vocals with expert resources at Choir Center.
        </p>
    
        {/* Search Bar */}
        <form
          onSubmit={handleSearch}
          className="w-full flex justify-center relative mb-6 overflow-visible"
          autoComplete="off"
        >
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyPress}
            className="w-full md:w-3/4 px-3 sm:px-4 py-3 rounded-xl bg-black/50 border border-green-500/50 focus:border-green-400 text-white text-lg outline-none placeholder-lime-200 shadow-lg transition-all font-medium"
            placeholder="Type and press Enter for full results…"
            ref={searchInputRef}
            style={{ fontFamily: `'Inter', 'Sora', sans-serif'` }}
          />
          {searchQuery && (
            <div className="text-xs text-lime-200 mt-1 animate-fade-in">
              Press <b>Enter</b> to see all results for "{searchQuery}"
            </div>
          )}
          {suggestions.length > 0 && (
            <ul
              className="absolute left-0 right-0 top-full bg-green-900/95 border border-green-500 rounded-b-xl text-left z-20 shadow-lg animate-fade-in"
              style={{ maxHeight: '320px', overflowY: 'auto' }}
            >
              {suggestions.map((suggestion, index) => (
                <li
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`px-4 py-2 cursor-pointer hover:bg-green-800/80 transition ${
                    index === selectedSuggestionIndex
                      ? 'bg-green-700/80 text-lime-200'
                      : 'text-white'
                  }`}
                  onMouseEnter={() => setSelectedSuggestionIndex(index)}
                  onMouseLeave={() => setSelectedSuggestionIndex(-1)}
                >
                  <span
                    className="font-semibold"
                    dangerouslySetInnerHTML={{
                      __html: suggestion.matches && suggestion.matches.length
                        ? highlight(suggestion.title, suggestion.matches, 'title')
                        : suggestion.title
                    }}
                  />
                  <span className="text-xs text-green-200">({suggestion.type})</span>
                </li>
              ))}
            </ul>
          )}
          <input type="text" name="honeypot" className="hidden" autoComplete="off" tabIndex={-1} />
        </form>
    
        {/* Call to Action Buttons */}
        <div className="flex flex-wrap gap-3 sm:gap-4 justify-center items-center mt-2">
          <Link
            to="/library"
            className="px-6 py-2 rounded-full bg-gradient-to-br from-green-700 via-lime-600 to-green-500 text-black font-bold text-base sm:text-lg shadow-xl hover:scale-105 hover:from-green-500 hover:to-lime-300 transition-all"
            style={{ fontFamily: `'Sora', 'Inter', sans-serif'` }}
          >
            Explore Choir Music
          </Link>
          <Link
            to="/blog"
            className="px-6 py-2 rounded-full bg-gradient-to-br from-lime-700 via-green-600 to-lime-400 text-black font-bold text-base sm:text-lg shadow-xl hover:scale-105 hover:from-green-500 hover:to-lime-200 transition-all"
            style={{ fontFamily: `'Sora', 'Inter', sans-serif'` }}
          >
            Read Choir Blog
          </Link>
          <Link
            to="/subscribe"
            className="px-6 py-2 rounded-full bg-gradient-to-br from-black via-green-700 to-black text-lime-100 font-bold text-base sm:text-lg shadow-xl hover:scale-105 hover:bg-green-900 hover:text-lime-300 transition-all"
            style={{ fontFamily: `'Sora', 'Inter', sans-serif'` }}
          >
            Subscribe
          </Link>
        </div>
      </div>
    </section>
    
    <main>
      
   {/* ADVERTISEMENT SECTION: Clean and Lightweight */}
          <section className="w-full flex justify-center py-4 bg-white border-b border-green-100 mb-4">
            <div className="w-full max-w-3xl flex items-center justify-center">
              <AdBanner position="home_above_sotw" />
            </div>
          </section>

{/* === FEATURED CHOIR SONG OF THE WEEK (2-Column) + LISTEN TO OTHER SONGS === */}
          <section
            className="
              w-full py-6 px-1 sm:px-3 md:px-6 my-5
              flex flex-col md:flex-row gap-4 md:gap-6 items-stretch
              bg-gradient-to-br from-lime-50 via-white to-green-50
              rounded-2xl shadow-2xl border border-green-200
              relative
            "
            style={{ minHeight: 360 }} // explicit minimum height
          >
            {/* Left: Featured Song of the Week + Gallery */}
            <div className="flex-1 w-full flex flex-col items-center justify-center">
              <h2 className="z-10 mb-4 w-full flex justify-center">
                <span className="inline-block px-5 py-2 rounded-xl bg-gradient-to-r from-black via-gray-800 to-green-900 text-white font-bold text-xl shadow-md">
                  Featured Choir Song of the Week
                </span>
              </h2>
          
              {/* Media Player */}
              {songOfTheWeek === null ? (
                <div className="w-full max-w-xs mx-auto py-6 flex flex-col items-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-200 via-lime-100 to-green-100 animate-pulse mb-4" />
                  <div className="h-5 bg-green-100 rounded w-2/3 mb-2 animate-pulse" />
                  <div className="h-4 bg-green-50 rounded w-1/2 animate-pulse" />
                </div>
              ) : songOfTheWeek ? (
                <div className="w-full flex flex-col items-center max-w-[420px] mx-auto my-1">
                  <div
                    className="
                      flex flex-col gap-1 px-2 py-2 rounded-xl
                      bg-gradient-to-br from-green-950 via-green-900 to-black
                      shadow-2xl ring-2 ring-green-600/70 hover:ring-4
                      w-full
                    "
                    style={{ maxWidth: 420, width: "100%", height: 90, margin: "0 auto", boxShadow: "none" }}
                  >
                    {/* ...player controls as before... */}
                    <div className="flex items-center gap-3 w-full">
                      <button
                        onClick={rewind}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-green-900 text-white shadow-lg hover:scale-110 hover:shadow-green-500/40 hover:bg-green-800 active:scale-95 focus:outline-none focus:ring-2 focus:ring-green-400"
                        aria-label="Rewind"
                      >⏪</button>
                      <button
                        onClick={togglePlay}
                        className="w-11 h-11 flex items-center justify-center rounded-full bg-gradient-to-br from-green-800 to-black text-2xl text-white font-bold shadow-lg hover:scale-110 hover:shadow-green-500/50 hover:bg-green-900 active:scale-95 focus:outline-none focus:ring-2 focus:ring-green-400"
                        aria-label={isPlaying ? "Pause featured choir song" : "Play featured choir song"}
                      >
                        {isPlaying ? '⏸' : '▶'}
                      </button>
                      <div className="flex-1 min-w-0 mx-1">
                        <div className="relative w-full overflow-hidden h-7 rounded">
                          <div
                            className="absolute whitespace-nowrap animate-marquee text-white font-semibold text-base md:text-lg drop-shadow"
                            style={{ animationDuration: "10s" }}
                          >
                            {songTitle}
                          </div>
                        </div>
                        <span className="block text-xs text-green-100 font-normal opacity-70 truncate text-center">
                          {songComposer}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center w-full mt-2">
                      <span className="text-xs text-white w-10 text-left">{formatTime(currentTime)}</span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={progress}
                        onChange={e => {
                          if (audioRef.current) {
                            const newTime = (e.target.value / 100) * audioRef.current.duration;
                            audioRef.current.currentTime = newTime;
                            setProgress(e.target.value);
                          }
                        }}
                        className="flex-1 mx-2 h-2 rounded-lg bg-black/40 accent-green-500 ring-1 ring-green-800 shadow-inner cursor-pointer"
                        aria-label="Audio progress for featured song"
                        style={{
                          accentColor: "#22c55e"
                        }}
                      />
                      <span className="text-xs text-white w-10 text-right">{formatTime(duration)}</span>
                      <audio ref={audioRef} src={songOfTheWeek} />
                    </div>
                  </div>
          
                  {/* Gallery Grid */}
                  <div
                    className="w-full flex flex-col items-center justify-center flex-1"
                    style={{
                      maxWidth: 420,
                      minHeight: 180,
                      height: '100%',
                    }}
                  >
                    <h3 className="text-green-700 text-base font-semibold mb-2 mt-4">
                      Choir Music Books
                    </h3>
                    <Link
                      to="/choir-music-books"
                      className="relative block w-full h-full rounded-xl overflow-hidden shadow-lg border-2 border-green-200 hover:shadow-2xl hover:border-green-400 transition-all"
                      style={{
                        minHeight: 180,
                        maxWidth: 420,
                        margin: "0 auto",
                        display: "block",
                      }}
                    >
                      <img
                        src="/gallery/choirmusicbooks.webp"
                        alt="Explore Choir Music Books"
                        className="w-full h-full object-cover object-center"
                        style={{ minHeight: 180, height: "100%", width: "100%", maxHeight: 240 }}
                        loading="lazy"
                        draggable={false}
                      />
                      <div
                        className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white text-base md:text-lg font-bold rounded-full px-6 py-2 shadow-lg backdrop-blur-sm z-10 w-11/12 text-center"
                      >
                        Explore All Choir Music Books
                      </div>
                    </Link>
                  </div>
                </div>
              ) : (
                <p className="text-green-500 text-center py-3">No choir song selected for this week.</p>
              )}
            </div>
          
            {/* Right: Listen to Other Songs */}
            <div className="flex-1 w-full max-w-md mx-auto md:h-full md:flex md:flex-col md:justify-center mt-8 md:mt-0">
              <h3 className="w-full flex justify-center">
                <span className="inline-block px-5 py-2 rounded-xl bg-gradient-to-r from-black via-gray-800 to-green-900 text-white font-bold text-lg shadow-md">
                  <svg className="w-6 h-6 text-green-400 inline-block mr-2 -mt-1" fill="none" viewBox="0 0 24 24">
                    <path d="M5 17V7a7 7 0 0 1 14 0v10" stroke="#22c55e" strokeWidth="2" />
                    <rect x="3" y="16" width="4" height="5" rx="2" fill="#22c55e" />
                    <rect x="17" y="16" width="4" height="5" rx="2" fill="#22c55e" />
                  </svg>
                  Listen to Other Songs
                </span>
              </h3>
              <ul className="space-y-3 mt-3">
                {audioSongs.length > 0 ? audioSongs.map((song, idx) => (
                  <li
                    key={song.id}
                    className="flex items-center gap-2 bg-white rounded-xl border border-green-100 px-3 py-2 shadow hover:bg-green-50 transition-all"
                  >
                    <button
                      className={`w-9 h-9 flex items-center justify-center rounded-full border-2 border-green-400
                        ${playingIndex === idx ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'}
                        shadow hover:scale-105 transition-all text-lg`}
                      aria-label={playingIndex === idx ? "Pause" : "Play"}
                      onClick={() => {
                        if (playingIndex === idx) {
                          document.getElementById(`audio-${idx}`).pause();
                          setPlayingIndex(-1);
                        } else {
                          audioSongs.forEach((_, i) => {
                            const el = document.getElementById(`audio-${i}`);
                            if (el) el.pause();
                          });
                          document.getElementById(`audio-${idx}`).play();
                          setPlayingIndex(idx);
                        }
                      }}
                    >
                      {playingIndex === idx ? '⏸' : '▶'}
                    </button>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/song/${song.permalink || song.id}`}
                        className="font-semibold text-green-900 hover:text-green-700 truncate block"
                        title={song.title}
                        onClick={() => {
                          if (window.gtag) {
                            window.gtag('event', 'song_audio_card_click', {
                              event_category: 'Home Listen Songs',
                              event_label: song?.title || 'Unknown Song',
                              song_id: song?.id,
                              song_permalink: song?.permalink,
                              value: 1,
                            });
                          }
                        }}
                      >
                        {song.title}
                      </Link>
                      <div className="text-xs text-green-600 truncate">{song.composer}</div>
                    </div>
                    <button
                      className="px-3 py-1 rounded-full bg-gradient-to-br from-green-700 via-lime-400 to-green-400 text-black text-xs font-semibold shadow hover:scale-105 transition-all"
                      onClick={() => {
                        if (window.gtag) {
                          gtag('event', 'file_download', {
                            event_category: 'Downloads',
                            event_label: song?.title + ' Audio' || 'Home Audio',
                            value: 1,
                          });
                        }
                        handleAudioDownload(song);
                      }}
                      aria-label="Download audio"
                    >
                      Download
                    </button>
                    <audio
                      id={`audio-${idx}`}
                      src={song.audio_url}
                      onEnded={() => setPlayingIndex(-1)}
                      className="hidden"
                    />
                  </li>
                )) : (
                  <li className="text-green-400 text-sm">No other songs with audio right now.</li>
                )}
              </ul>
            </div>
          </section>

{/* === ERROR ALERT: Shows when an error occurs during data fetch === */}
          {error && (
            <div className="my-4 flex items-center justify-center">
              <div className="flex items-center gap-2 bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-xl shadow-md max-w-xl w-full text-center">
                {/* Error Icon */}
                <svg className="h-5 w-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                </svg>
                <span className="font-semibold">{error}</span>
              </div>
            </div>
          )}

{/* SLIM QUICK ACTION BUTTONS SECTION */}
          <section className="w-full flex justify-center py-1 my-4">
            <div className="
                w-full
                grid grid-cols-2 md:grid-cols-4
                gap-3 md:gap-5
                items-center
                px-2
                " 
                style={{ maxWidth: 960 }}
            >
              <Link
                to="/choir-music-books"
                className="flex items-center justify-center px-4 py-2 md:px-5 md:py-3 rounded-xl bg-gradient-to-br from-green-900 via-green-800 to-green-700
                  text-white font-extrabold text-sm md:text-base shadow-lg hover:scale-105 hover:shadow-xl transition-all whitespace-nowrap"
                style={{ fontFamily: `'Sora', 'Inter', sans-serif'` }}
              >
                <span className="mr-2 text-lg md:text-xl">📚</span> More Choir Books
              </Link>
              <Link
                to="/choir-games"
                className="flex items-center justify-center px-4 py-2 md:px-5 md:py-3 rounded-xl bg-gradient-to-br from-yellow-400 via-yellow-300 to-yellow-200
                  text-green-900 font-extrabold text-sm md:text-base shadow-lg hover:scale-105 hover:shadow-xl transition-all whitespace-nowrap border border-green-200"
                style={{ fontFamily: `'Sora', 'Inter', sans-serif'` }}
              >
                <span className="mr-2 text-lg md:text-xl">🎲</span> Choir Games
              </Link>
              <Link
                to="/selections-manager"
                className="flex items-center justify-center px-4 py-2 md:px-5 md:py-3 rounded-xl bg-gradient-to-br from-lime-200 via-green-100 to-lime-300
                  text-green-900 font-extrabold text-sm md:text-base shadow-lg hover:scale-105 hover:shadow-xl transition-all whitespace-nowrap border border-green-300"
                style={{ fontFamily: `'Sora', 'Inter', sans-serif'` }}
              >
                <span className="mr-2 text-lg md:text-xl">🗂️</span> Selections Manager
              </Link>
              <Link
                to="/choir-events"
                className="flex items-center justify-center px-4 py-2 md:px-5 md:py-3 rounded-xl bg-gradient-to-br from-green-900 via-green-700 to-green-800
                  text-yellow-200 font-extrabold text-sm md:text-base shadow-lg hover:scale-105 hover:shadow-xl transition-all whitespace-nowrap border border-yellow-100"
                style={{ fontFamily: `'Sora', 'Inter', sans-serif'`, color: "#FFE066" }}
              >
                <span className="mr-2 text-lg md:text-xl">📅</span> Choir Events
              </Link>
            </div>
          </section>

{/* === LATEST FREE CHOIR SONGS SECTION (with Tailwind Skeleton Loader) === */}
            <section className="w-full max-w-5xl mx-auto my-8 px-2 sm:px-4">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-green-900 mb-1 text-center">
                Latest Free Choir Songs
              </h2>
              <p className="text-green-800 text-base sm:text-lg mb-5 text-center max-w-2xl mx-auto">
                Explore our latest free choir songs in solfa notation, perfect for your next rehearsal.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Loader Skeleton while loading */}
                {loading ? (
                  <>
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="flex flex-col rounded-2xl border border-green-200 bg-white shadow p-5 animate-pulse gap-2">
                        <div className="w-2/3 h-6 bg-green-100 rounded mb-2 mx-auto" />
                        <div className="w-1/2 h-4 bg-green-50 rounded mb-2 mx-auto" />
                        <div className="w-full h-8 bg-green-50 rounded mt-4" />
                      </div>
                    ))}
                  </>
                ) : filteredSongs.length > 0 ? (
                  filteredSongs.map((song, index) => (
                    <article
                      key={song.id}
                      className="
                        relative flex flex-col justify-between cursor-pointer
                        rounded-2xl border border-green-200 shadow
                        bg-white
                        transition-transform duration-200 hover:scale-105 hover:shadow-lg
                        overflow-hidden
                      "
                      onClick={() => {
                        if (window.gtag) {
                          window.gtag('event', 'song_card_click', {
                            event_category: 'Home Latest Songs',
                            event_label: song?.title || 'Unknown Song',
                            song_id: song?.id,
                            song_permalink: song?.permalink,
                            value: 1,
                          });
                        }
                        handleSongClick(song);
                      }}
                    >
                      {/* Floating Neon Blur Effect (top right) */}
                      <div className="absolute -top-5 -right-5 w-20 h-20 rounded-full bg-lime-200/40 blur-2xl z-0 pointer-events-none"></div>
            
                      {/* Card Content */}
                      <div className="relative z-10 p-4 pb-3">
                        <h3 className="font-bold text-lg md:text-xl text-green-900 mb-1 text-center">
                          <Link to={`/song/${song.permalink || song.id}`}>
                            <span
                              dangerouslySetInnerHTML={{
                                __html:
                                  suggestions?.find(s => s.id === song.id && s.type === 'song')?.matches?.length
                                    ? highlight(song.title, suggestions.find(s => s.id === song.id && s.type === 'song').matches, 'title')
                                    : song.title
                              }}
                            />
                          </Link>
                        </h3>
                        <p className="text-green-700 text-sm mb-2 text-center">
                          {song.composer || 'Unknown Composer'}
                        </p>
                        {song.audio_url && (
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <span
                              className="
                                flex items-center gap-1 px-2 py-1 rounded-full
                                bg-lime-50 text-lime-700 text-xs font-semibold border border-lime-200
                                shadow
                                transition
                              "
                            >
                              <span className="mr-1">Audio Available</span>
                              {/* Headphone Icon */}
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                                <path d="M4 13v-2a8 8 0 1 1 16 0v2" stroke="#a3e635" strokeWidth="1.5" fill="none"/>
                                <rect x="3" y="13" width="4" height="6" rx="2" fill="#a3e635" />
                                <rect x="17" y="13" width="4" height="6" rx="2" fill="#a3e635" />
                              </svg>
                            </span>
                          </div>
                        )}
                        <p className="text-green-800/80 text-xs mb-1 text-center">
                          Downloaded <b>{song.downloads || 0}</b> times
                        </p>
                        <p className="text-green-800/60 text-xs text-center">
                          Added {song.created_at
                            ? new Date(song.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'Unknown Date'}
                        </p>
                      </div>
                      <div className="relative z-10 flex gap-2 px-4 pb-3">
                        <button
                          className="
                            flex-1 py-1 rounded-xl
                            bg-gradient-to-br from-green-600 via-lime-400 to-green-400
                            text-black text-sm font-bold shadow hover:scale-105 transition-all
                          "
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.gtag) {
                              gtag('event', 'file_download', {
                                event_category: 'Downloads',
                                event_label: song?.title || 'Home PDF',
                                value: 1,
                              });
                            }
                            handleDownload(song);
                          }}
                          aria-label={`Download free choir song ${song.title}`}
                        >
                          Download
                        </button>
                        <button
                          className="
                            flex-1 py-1 rounded-xl
                            bg-gradient-to-br from-black/80 via-green-600 to-lime-600
                            text-lime-100 text-sm font-bold shadow hover:scale-105 transition-all
                          "
                          onClick={(e) => { e.stopPropagation(); handleShare(song.title, song.permalink || song.id); }}
                          aria-label={`Share choir song ${song.title}`}
                        >
                          Share
                        </button>
                      </div>
                    </article>
                  ))
                ) : (
                  // Empty state when not loading and no songs
                  <p className="col-span-full text-center text-green-500">No free choir songs available.</p>
                )}
              </div>
              <div className="mt-6 flex justify-center">
                <Link
                  to="/library"
                  className="
                    px-6 py-2 rounded-full font-bold
                    bg-gradient-to-r from-green-600 via-lime-400 to-green-400
                    text-black shadow-lg hover:scale-105 transition-all
                  "
                >
                  Browse More Free Choir Songs
                </Link>
              </div>
            </section>

        <hr className="section-separator" />
      
{/* === LATEST CHOIR BLOG POSTS SECTION (with Tailwind Skeleton Loader) === */}
            <section className="w-full max-w-5xl mx-auto my-10 px-2 sm:px-4">
              <h2 className="section-title text-2xl sm:text-3xl font-extrabold text-green-900 mb-1 text-center">
                Latest Choir Blog Posts
              </h2>
              <p className="text-green-800 text-base sm:text-lg mb-6 text-center max-w-2xl mx-auto">
                Read our freshest choir blog posts on tips, vocal techniques, and music resources—carefully curated to inspire your next choir adventure!
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Loader Skeleton */}
                {loading ? (
                  Array(4).fill(0).map((_, i) => (
                    <div key={i} className="rounded-2xl border border-green-100 bg-white/80 shadow p-4 animate-pulse flex flex-col gap-2">
                      <div className="h-5 w-2/3 bg-green-100 rounded mb-2 mx-auto" />
                      <div className="h-4 w-1/2 bg-green-50 rounded mb-2 mx-auto" />
                      <div className="h-7 w-1/2 bg-green-50 rounded mx-auto" />
                    </div>
                  ))
                ) : filteredPosts.length > 0 ? (
                  filteredPosts.map((post) => (
                    <Link
                      to={`/blog/${post.permalink || `post-${post.id}`}`}
                      key={post.id}
                      className="..."
                      onClick={() => {
                        if (window.gtag) {
                          window.gtag('event', 'blog_card_click', {
                            event_category: 'Home Blog Posts',
                            event_label: post?.title || 'Unknown Blog',
                            blog_id: post?.id,
                            blog_permalink: post?.permalink,
                            value: 1,
                          });
                        }
                      }}
                    >
                      <div className="relative z-10">
                        <h3 className="font-bold text-lg md:text-xl text-green-900 group-hover:text-lime-700 mb-2 text-center transition-colors duration-100">
                          {post.title}
                        </h3>
                        <div className="flex justify-center mt-2">
                          <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-semibold shadow-sm">
                            Blog
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="col-span-full text-green-500 text-center py-5">
                    No choir blog posts available.
                  </p>
                )}
              </div>
              <div className="mt-8 flex justify-center">
                <Link
                  to="/blog"
                  className="
                    px-6 py-2 rounded-full font-bold
                    bg-gradient-to-r from-green-600 via-lime-400 to-green-400
                    text-black shadow
                    hover:from-green-700 hover:to-lime-500 transition-colors duration-100
                  "
                >
                  Read More Choir Blog Posts
                </Link>
              </div>
            </section>

        <hr className="section-separator" />
      
{/* === CHOIR MUSIC LIBRARY STATISTICS SECTION (Simple, Modern, Animated) === */}
            <section className="w-full max-w-4xl mx-auto my-12 px-2 sm:px-6">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-green-900 mb-5 text-center">
                Choir Music Library Statistics
              </h2>
              <div className="
                grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10
                bg-white rounded-3xl shadow-xl border border-green-200
                py-10 px-4 sm:px-8
              ">
                {/* STAT: Total Songs */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 via-lime-200 to-green-200 shadow-xl flex items-center justify-center mb-2 animate-bounce-slow hover:scale-105 transition-transform duration-200">
                    {/* Music Notes SVG */}
                    <svg className="w-11 h-11 text-green-800 animate-musicnote" fill="none" viewBox="0 0 24 24">
                      <path d="M9 18V5l10-2v11" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="7" cy="18" r="3" fill="currentColor" fillOpacity=".13"/>
                      <circle cx="17" cy="18" r="3" fill="currentColor" fillOpacity=".13"/>
                    </svg>
                  </div>
                  <span className="block text-4xl md:text-5xl font-extrabold text-green-900 animate-count">
                    {songsCount}+
                  </span>
                  <span className="block text-base font-medium text-green-900/90 mt-1">
                    Total Songs
                  </span>
                  <span className="sr-only">{siteStats.totalSongs} songs in library</span>
                </div>
            
                {/* STAT: Total Downloads */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-200 via-yellow-100 to-yellow-300 shadow-xl flex items-center justify-center mb-2 animate-bounce-slow hover:scale-105 transition-transform duration-200">
                    {/* Download Arrow SVG */}
                    <svg className="w-11 h-11 text-yellow-700 animate-downloadarrow" fill="none" viewBox="0 0 24 24">
                      <path d="M12 21V3m0 0l4 4m-4-4l-4 4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="18.5" r="3" fill="currentColor" fillOpacity=".10"/>
                    </svg>
                  </div>
                  <span className="block text-4xl md:text-5xl font-extrabold text-yellow-700 animate-count">
                    {downloadsCount}+
                  </span>
                  <span className="block text-base font-medium text-yellow-800/90 mt-1">
                    Total Downloads
                  </span>
                  <span className="sr-only">{siteStats.totalDownloads} downloads</span>
                </div>
              </div>
            
              {/* Tailwind custom keyframes for the fun SVG animation - add this to your styles.css if not already present */}
              <style>
                {`
                  @keyframes musicnote {
                    0%, 100% { transform: translateY(0) rotate(-7deg);}
                    50% { transform: translateY(-6px) rotate(7deg);}
                  }
                  .animate-musicnote { animation: musicnote 1.6s cubic-bezier(.5,-0.5,.5,1.5) infinite; }
                  @keyframes downloadarrow {
                    0%, 100% { transform: translateY(0);}
                    30% { transform: translateY(5px);}
                    60% { transform: translateY(-2px);}
                    80% { transform: translateY(3px);}
                  }
                  .animate-downloadarrow { animation: downloadarrow 1.8s cubic-bezier(.5,-0.5,.5,1.5) infinite; }
                  @keyframes bounce-slow {
                    0%,100% { transform: translateY(0);}
                    50% { transform: translateY(-10px);}
                  }
                  .animate-bounce-slow { animation: bounce-slow 2.6s infinite cubic-bezier(.5,0,.5,1); }
                  .animate-count { transition: color .35s, text-shadow .35s; }
                `}
              </style>
            </section>

{/* === RECENT, TOP CHOIR SONG DOWNLOADS & TOP BLOG POSTS SECTION (Responsive 3-Column) === */}
            <section className="w-full my-7 py-4 px-2 sm:px-4 rounded-2xl shadow-lg bg-gradient-to-br from-green-50 via-white to-lime-50 border border-green-100">
              <h2 className="section-title text-green-800 text-xl sm:text-2xl font-bold mb-5 text-center animate-text">
                Recent & Top Choir Song Downloads & Blog Posts
              </h2>
              <div className="flex flex-col md:grid md:grid-cols-3 gap-6">
                {/* Left Column: Recent Downloads */}
                <div className="flex-1 bg-white/80 rounded-xl shadow p-3 md:p-4 border border-green-100">
                  <h3 className="text-green-800 font-semibold text-lg mb-2 flex items-center gap-1">
                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 19V6M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Recent Downloads
                  </h3>
                  {recentDownloads.length > 0 ? (
                    <ul className="divide-y divide-green-100">
                      {recentDownloads.map((download) => (
                        <li key={download.id} className="py-2 flex flex-col">
                          <span className="font-medium text-green-900">
                            <Link to={`/song/${download.permalink || download.id}`} className="hover:underline">
                              {download.title}
                            </Link>
                          </span>
                          <span className="text-xs text-green-800">{download.composer || 'Unknown Composer'}</span>
                          <span className="text-xs text-gray-400">{download.updated_at || download.created_at ? calculateTimeAgo(download.updated_at || download.created_at) : ''}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-green-400">No recent downloads yet.</p>
                  )}
                </div>
                {/* Center Column: Top Downloaded Songs */}
                <div className="flex-1 bg-white/80 rounded-xl shadow p-3 md:p-4 border border-yellow-100">
                  <h3 className="text-yellow-700 font-semibold text-lg mb-2 flex items-center gap-1">
                    <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 17.75L18.16 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.48 4.73L5.84 21z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Top Downloaded Songs
                  </h3>
                  {topSongs.length > 0 ? (
                    <ul className="divide-y divide-yellow-100">
                      {topSongs.map((song) => (
                        <li key={song.id} className="py-2 flex flex-col">
                          <span className="font-medium text-yellow-900">
                            <Link to={`/song/${song.permalink || song.id}`} className="hover:underline">
                              {song.title}
                            </Link>
                          </span>
                          <span className="text-xs text-yellow-800">{song.composer || 'Unknown Composer'}</span>
                          <span className="text-xs text-gray-400">Downloads: <span className="font-bold">{song.downloads}</span></span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-yellow-500">No top downloads yet.</p>
                  )}
                </div>
                {/* Right Column: Top Blog Posts */}
                <div className="flex-1 bg-white/80 rounded-xl shadow p-3 md:p-4 border border-lime-100">
                  <h3 className="text-lime-700 font-semibold text-lg mb-2 flex items-center gap-1">
                    <svg className="w-5 h-5 text-lime-400" fill="none" viewBox="0 0 24 24">
                      <rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="2" fillOpacity=".13"/>
                      <path d="M7 9h10M7 13h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                    </svg>
                    Top Blog Posts
                  </h3>
                  {filteredPosts && filteredPosts.length > 0 ? (
                    <ul className="divide-y divide-lime-100">
                      {filteredPosts.slice(0, 4).map((post) => (
                        <li key={post.id} className="py-2 flex flex-col">
                          <span className="font-medium text-lime-900">
                            <Link
                              to={`/blog/${post.permalink || `post-${post.id}`}`}
                              className="hover:underline"
                            >
                              {post.title}
                            </Link>
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-lime-400">No blog posts yet.</p>
                  )}
                </div>
              </div>
            </section>

      {/* === SOLFA NOTATION / CHOIR LIBRARY TIP BANNER (Modern, SEO-Friendly) === */}
            <section
              aria-label="Solfa Notation Guide and Choir Library"
              className="w-full max-w-2xl mx-auto my-8 py-4 px-4 rounded-2xl shadow-md bg-gradient-to-br from-green-50 via-white to-lime-50 flex flex-col items-center border border-green-200"
            >
              <h3 className="font-bold text-green-800 text-lg sm:text-xl mb-2 animate-pulse-slow">
                New to <span className="text-green-900">Solfa Notation</span> or exploring choir songs?
              </h3>
              <div className="flex flex-wrap gap-2 items-center justify-center text-base sm:text-lg text-green-900 font-medium">
                <span>🎵</span>
                <Link
                  to="/blog/what-is-solfa-notation"
                  className="underline underline-offset-4 hover:text-green-900 transition"
                >
                  Read our Beginner’s Guide
                </Link>
                <span className="mx-1">or</span>
                <Link
                  to="/library"
                  className="underline underline-offset-4 hover:text-green-900 transition"
                >
                  Explore the Full Choir Library
                </Link>
                <span>🎼</span>
              </div>
              <p className="mt-2 text-green-800 text-center text-sm sm:text-base max-w-lg">
                Discover free solfa notation choir songs, download sheet music, and find vocal training resources to enhance your choir experience!
              </p>
            </section>

{/* === MODERN FAQ SECTION: About Choir Center === */}
            <section className="w-full max-w-3xl mx-auto my-12 px-3">
              <h2 className="section-title text-2xl sm:text-3xl font-bold text-green-900 text-center mb-6 animate-text">
                Frequently Asked Questions <span className="text-green-800">🎶</span>
              </h2>
              <div className="space-y-4">
                {[
                  {
                    question: "What is Choir Center and who is it for?",
                    answer: "Choir Center is a free online platform for choir singers, directors, and music lovers. We offer downloadable solfa notation choir songs, rehearsal tips, and music learning resources for both beginners and experienced choristers."
                  },
                  {
                    question: "Are the choir songs really free to download?",
                    answer: "Yes! Every solfa notation choir song on our site is free to download for practice, worship, and performance. You can access and print sheet music anytime. Terms and conditions apply."
                  },
                  {
                    question: "How do I download a choir song?",
                    answer: "Simply browse the choir library, click on any song, and use the Download button. Some downloads may require free registration for security and to help us keep offering great music."
                  },
                  {
                    question: "What is solfa notation?",
                    answer: "Solfa notation (do, re, mi, fa, sol, la, ti) is a simple way to read and sing music. If you're new, start with our beginner guide, and enjoy easy-to-follow choir sheet music!"
                  },
                  {
                    question: "Can I request or submit a choir song?",
                    answer: "Yes! Use our contact form or email us to request new songs or submit your own compositions. We love featuring community submissions from all traditions."
                  },
                  {
                    question: "Do you offer choir rehearsal or vocal training tips?",
                    answer: "Absolutely. Check our blog for expert tips on sight-singing, conducting, voice training, and music theory, all written for choirs and vocal groups."
                  },
                  {
                    question: "Is Choir Center mobile-friendly?",
                    answer: "Yes! Our website works great on phones, tablets, and computers. Download choir songs, follow lessons, and practice anywhere, anytime."
                  }
                ].map((faq, index) => (
                  <div
                    key={index}
                    className={`
                      rounded-xl shadow-md border border-green-200 bg-white
                      transition-all duration-200
                      ${expandedFaq === index ? "ring-2 ring-green-500/60 shadow-lg" : ""}
                    `}
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(index)}
                      className="w-full flex justify-between items-center px-4 py-4 text-lg font-medium text-green-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                      aria-expanded={expandedFaq === index}
                      aria-controls={`faq-content-${index}`}
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                        </svg>
                        {faq.question}
                      </span>
                      <span className={`
                        text-green-800 text-xl ml-2
                        transform transition-transform duration-200
                        ${expandedFaq === index ? "rotate-180" : ""}
                      `}>
                        ▼
                      </span>
                    </button>
                    <div
                      id={`faq-content-${index}`}
                      className="overflow-hidden transition-all duration-300 ease-in-out"
                      style={{
                        maxHeight: expandedFaq === index ? "300px" : "0",
                        padding: expandedFaq === index ? "0 1.5rem 1rem 1.5rem" : "0 1.5rem"
                      }}
                    >
                      <div className={`text-green-800 text-base leading-relaxed ${expandedFaq === index ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

</main>

{audioModalOpen && selectedAudioSong && (
  <AudioDownloadModal
    onClose={() => setAudioModalOpen(false)}
    onSubscribe={handleSubscribe}   // <-- Pass this prop
    song={selectedAudioSong}
    user={user}
  />
)}
      
{showPrompt && (
  <DownloadPromptModal
    onClose={() => setShowPrompt(false)}
    onRegister={handleRegister}
    onSubscribe={handleSubscribe}
    type={promptType}
  />
)}

    </>
  );
}

export default Home;
